import './index.less';

import { Button, ConfigProvider, Input, Modal, Select, Space, Table, Tag } from 'antd';
import startCase from 'lodash.startcase';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { createBreakpoint, useLifecycles } from 'react-use';

import { PageHeader } from '@ant-design/pro-components';
import { disbursementAgreementsStateReset } from '../../../../features/disbursement/disbursementSlice';
import { numberWithCommas } from '../../../../helpers/number';
import { AppDispatch, RootState } from '../../../../store/store';
import { onCalculateNetDisbursementAmount } from '../../../../utils/calculation.util';
import { displayDate } from '../../../../utils/datetime.util';

import type { ColumnsType, TableProps } from 'antd/es/table';
import { SmeAgreement } from '../../../../features/smeAgreement/smeAgreementSlice';
import { SME_DISBURSEMENT_BATCH_STATUS } from '../../../../enum/smeApplicationStatus';
import {
  getSmeDisbursementBatchAgreementsThunk,
  getSmeDisbursementBatchByIdThunk,
} from '../../../../services/smeDisbursementService/smeDisbursementThunk';
import { SorterResult } from 'antd/es/table/interface';
import UpdateDisbursementModal from './UpdateDisbursementModal';

const { Search } = Input;

const useBreakpoint = createBreakpoint();

const SmeBatchDetailsPage = () => {
  const [tableLoading, setTableLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'tablet';

  const { smeDisbursementBatchAgreements, smeDisbursementBatchDetails } = useSelector(
    (state: RootState) => state.smeDisbursement
  );

  const { batchId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const pageNum = queryParams.get('pageNum');
  const pageSize = queryParams.get('pageSize');

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: pageNum ? +pageNum : 1,
        pageSize: pageSize ? +pageSize : 20,
      },
      sortOrder: 'desc',
      sortField: 'createdAt',
      search: {},
      filters: {},
    }),
    []
  );

  const [{ confirm }, contextHolder] = Modal.useModal();
  const [paginationState, setPaginationState] = useState(initialParams.pagination);
  const [searchType, setSearchType] = useState('agreementNo');
  const [searchValue, setSearchValue] = useState('');
  const [sortedInfo, setSortedInfo] = useState<SorterResult<any>>({});

  const tablePagination = {
    total: smeDisbursementBatchAgreements?.data?.total,
    showSizeChanger: true,
    showTotal: (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} items`,
    current: paginationState.current,
    pageSize: paginationState.pageSize,
  };

  const batchDetailsColumns: ColumnsType<any> = [
    {
      title: 'Signed Agreement Date',
      dataIndex: 'signDate',
      key: 'signDate',
      sorter: (a, b) => a.signDate - b.signDate,
      sortOrder: sortedInfo.columnKey === 'signDate' ? sortedInfo.order : null,
      render: (text) => (text ? displayDate(text) : '-'),
    },
    {
      title: 'Agreement No',
      dataIndex: 'agreementNo',
      key: 'agreementNo',
      render: (text, record) => (
        <Button type="link" onClick={() => navigate(`/sme/agreement/details/${record.id}`)}>
          <span style={{ userSelect: 'text' }}>{text}</span>
        </Button>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) =>
        record?.smeApplication?.smeIndividualApplication?.name ??
        record?.smeApplication?.smeSolePropApplication?.name ??
        record?.smeApplication?.smePartnershipApplication?.name ??
        record?.smeApplication?.smeCompanyApplication?.companyName ??
        record?.smeApplication?.smeStaffApplication?.name ??
        '-',
    },
    {
      title: 'Organization Type',
      dataIndex: 'organizationType',
      key: 'organizationType',
      render: (_, record) => record?.smeApplication?.organizationType ?? '-',
    },
    {
      title: 'Approved Loan Amount',
      dataIndex: 'systemLoanAmountSuggested',
      key: 'systemLoanAmountSuggested',
      render: (_, record) =>
        record.smeApplication?.financeAmount != null
          ? `RM ${numberWithCommas(+record.smeApplication?.financeAmount ?? 0)}`
          : '-',
    },
    {
      title: 'Net Disbursement Amount',
      dataIndex: 'disbursementAmount',
      key: 'disbursementAmount',
      render: (text, record) =>
        `RM ${numberWithCommas(
          onCalculateNetDisbursementAmount(
            +record.smeApplication?.financeAmount ?? 0,
            +record.smeApplication?.processingFee ?? 0,
            +record.smeApplication?.stampingDutyPercentage ?? 0,
            +record.smeApplication?.processingFeeToDiscount ?? 0,
            0,
            0,
            record.smeApplication?.status
          )
        )}`,
    },
    {
      title: 'Update',
      key: 'update',
      render: (text, record) => (
        <>
          <UpdateDisbursementModal
            key={record.id}
            batchId={batchId}
            agreement={record}
            callback={updateDisbursementCallback}
          />
        </>
      ),
    },
  ];

  const completedTableColumns: ColumnsType<SmeAgreement> = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Posted Date',
      dataIndex: 'disbursementPostedDate',
      key: 'postedDate',
      render: (text) => (text ? displayDate(text) : '-'),
    },
  ];

  const columns = [
    ...batchDetailsColumns,
    ...(smeDisbursementBatchDetails?.data?.status === SME_DISBURSEMENT_BATCH_STATUS.COMPLETED ||
    smeDisbursementBatchDetails?.data?.status === SME_DISBURSEMENT_BATCH_STATUS.INCOMPLETE
      ? completedTableColumns
      : []),
  ];

  const fetchDisbursementAgreements = async (params?: any) => {
    await dispatch(
      getSmeDisbursementBatchAgreementsThunk({
        ...initialParams,
        params,
        id: batchId,
      })
    );
  };

  const fetchDisbursementBatchDetails = async (params?: any) => {
    await dispatch(
      getSmeDisbursementBatchByIdThunk({
        ...initialParams,
        ...params,
        id: batchId,
      })
    );
  };

  const updateDisbursementCallback = async () => {
    await fetchDisbursementBatchDetails(batchId);
    await fetchDisbursementAgreements();
  };

  const onSearch = (value: string) => {
    value = value.trim();
    fetchDisbursementAgreements({
      ...initialParams,
      search: { [searchType]: value },
    });

    setSearchValue(value);
  };

  const handleTableChange: TableProps<SmeAgreement>['onChange'] = async (
    pagination,
    filters,
    sorter: any
  ) => {
    fetchDisbursementAgreements({ pagination });
    setSortedInfo(sorter as SorterResult<SmeAgreement>);
    setPaginationState({
      current: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 10,
    });
  };

  useLifecycles(
    () => {
      fetchDisbursementAgreements();
      fetchDisbursementBatchDetails(batchId);
    },
    () => {
      dispatch(disbursementAgreementsStateReset());
    }
  );

  return (
    <>
      {contextHolder}
      <Helmet>
        <title>Disbursement Details - redCASH CEP</title>
      </Helmet>

      <PageHeader
        className="px-0 mb-2"
        title="Disbursement Details"
        onBack={() => navigate(-1)}
        subTitle={
          <Space size="middle">
            <span>{`Batch No. ${smeDisbursementBatchDetails?.data?.batchNo}`}</span>
            {smeDisbursementBatchDetails?.data?.batchNo && (
              <>
                <Tag
                  color={
                    smeDisbursementBatchDetails?.data?.status ===
                    SME_DISBURSEMENT_BATCH_STATUS.INCOMPLETE
                      ? 'error'
                      : smeDisbursementBatchDetails?.data?.status ===
                        SME_DISBURSEMENT_BATCH_STATUS.COMPLETED
                      ? 'success'
                      : 'default'
                  }
                >
                  {startCase(
                    smeDisbursementBatchDetails?.data?.status ===
                      SME_DISBURSEMENT_BATCH_STATUS.INCOMPLETE
                      ? 'INCOMPLETE'
                      : smeDisbursementBatchDetails?.data?.status
                  )}
                </Tag>
              </>
            )}
          </Space>
        }
      />

      <div className="flex justify-between mb-4">
        <Input.Group compact>
          <Select
            defaultValue="agreementNo"
            style={{ width: 160 }}
            options={[
              { value: 'agreementNo', label: 'Agreement No.' },
              { value: 'applicationNo', label: 'Application No' },
              { value: 'name', label: 'Name' },
            ]}
            onChange={(value) => setSearchType(value)}
          ></Select>
          <Search placeholder="input search text" onSearch={onSearch} style={{ width: 240 }} />
        </Input.Group>
      </div>

      <ConfigProvider theme={{ token: { colorPrimary: '#8F8F8F' } }}>
        <Table
          rowKey="id"
          className="min-w-fit"
          columns={columns}
          pagination={tablePagination}
          dataSource={smeDisbursementBatchAgreements?.data?.data}
          loading={smeDisbursementBatchAgreements?.isLoading || tableLoading}
          onChange={handleTableChange}
        />
      </ConfigProvider>
    </>
  );
};

export default SmeBatchDetailsPage;
